// src/components/FilterBar.tsx
import React, { useState } from "react";
import { Button, Input, Select, Modal, Radio, Space, DatePicker, InputNumber, Spin } from 'antd';
import { DownOutlined, EditOutlined, LoadingOutlined } from '@ant-design/icons';
import moment from "moment";
import "./FilterBar.css";
import dayjs, { Dayjs } from "dayjs";

const { Option } = Select;
const { Group: RadioGroup } = Radio;

export interface FilterOption {
  name: string; // The key for the filter
  label: string; // The display label for the filter
  multiple?: boolean;
  disabled?: boolean; // Whether the filter is disabled
  type: "text" | "select" | "timerange"; // Type of filter
  options?: string[]; // For select type filters
}

interface FilterBarProps {
  filters: Record<string, any>; // Current filter values. For select filters, expect an array of strings.
  filterOptions: FilterOption[]; // Configuration for available filters
  onFilterChange: (key: string, value: any) => void; // Callback for when a filter changes
  onSortChange: (sortBy: string, sortOrder: 'asc'|'desc') => void;
  onApplyFilters: () => void; // Callback for applying filters
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  isLoading?: boolean; // Indicates if filter options are still loading
  sortOptions?: { value: string; label: string }[];
}

const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  filterOptions,
  onFilterChange,
  onSortChange,
  onApplyFilters,
  sortBy,
  sortOrder,
  sortOptions,
  isLoading = false,
}) => {
  // State for the time range dialog
  const [timeRangeModalVisible, setTimeRangeModalVisible] = useState(false);
  const [activeTimeFilter, setActiveTimeFilter] = useState<string>('');
  const [rangeType, setRangeType] = useState<'custom' | 'predefined'>('custom');
  const [startMode, setStartMode] = useState<'relative' | 'specific'>('relative');
  const [endMode, setEndMode] = useState<'specific'>('specific');
  const [relativeDays, setRelativeDays] = useState<number>(7);
  const [relativeUnit, setRelativeUnit] = useState<string>('Days Before');
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  // Clears all filters by iterating over each filter option and setting its value to an empty state.
  const handleClearAll = () => {
    filterOptions.forEach((filter) => {
      let emptyValue;
      switch (filter.type) {
        case "select":
          // Check if it's single select or multiple
          emptyValue = filter.multiple === false ? undefined : [];
          break;
        case "timerange":
          emptyValue = []; // Use an empty array for a range filter.
          break;
        case "text":
        default:
          emptyValue = "";
      }
      onFilterChange(filter.name, emptyValue);
    });
  };

  // Open the time range dialog
  const openTimeRangeModal = (filterName: string) => {
    const filterValue = filters[filterName] || [];
    
    // Set initial values from existing filter if available
    if (filterValue.length === 2) {
      setEndDate(dayjs(filterValue[1]));
      
      // If start date looks like a relative date setup
      if (filterValue[0].startsWith('relative:')) {
        setStartMode('relative');
        const relativeParts = filterValue[0].substring(9).split(':');
        setRelativeDays(parseInt(relativeParts[0]) || 7);
        setRelativeUnit(relativeParts[1] || 'Days Before');
      } else {
        setStartMode('specific');
        setStartDate(dayjs(filterValue[0]));
      }
    } else {
      // Default values
      setStartMode('relative');
      setRelativeDays(7);
      setRelativeUnit('Days Before');
      setEndDate(dayjs());
      setStartDate(null);
    }
    
    setActiveTimeFilter(filterName);
    setTimeRangeModalVisible(true);
  };

  // Apply the time range selection
  const handleTimeRangeApply = () => {
    let filterValues: string[] = [];

    if (startMode === 'relative') {
      // Compute the actual start date based on the relative value.
      const computedStartDate = endDate
        ? endDate.subtract(relativeDays, 'day')
        : dayjs().subtract(relativeDays, 'day');
      filterValues = [
        computedStartDate.format('YYYY-MM-DD HH:mm:ss'),
        endDate ? endDate.format('YYYY-MM-DD HH:mm:ss') : dayjs().format('YYYY-MM-DD HH:mm:ss')
      ];
    } else {
      // Store specific start and end dates
      if (startDate && endDate) {
        filterValues = [
          startDate.format('YYYY-MM-DD HH:mm:ss'),
          endDate.format('YYYY-MM-DD HH:mm:ss')
        ];
      }
    }

    if (filterValues.length === 2) {
      onFilterChange(activeTimeFilter, filterValues);
    }

    setTimeRangeModalVisible(false);
  };

  // Format the display value for the timerange filter dropdown
  const getTimeRangeDisplay = (filterName: string) => {
    const value = filters[filterName] || [];
    if (value.length !== 2) return 'No filter';
    
    // Handle relative dates
    if (value[0].startsWith('relative:')) {
      const relativeParts = value[0].substring(9).split(':');
      const days = parseInt(relativeParts[0]) || 7;
      const unit = relativeParts[1] || 'Days Before';
      const endDateStr = dayjs(value[1]).format('YYYY-MM-DD');
      
      // Calculate actual date range for display
      const endDateObj = dayjs(value[1]);
      const startDateObj = endDateObj.subtract(days, 'day');
      
      return `${startDateObj.format('YYYY-MM-DD HH:mm:ss')} - ${endDateObj.format('YYYY-MM-DD HH:mm:ss')}`;
    }
    
    // Handle specific dates
    const startDateStr = dayjs(value[0]).format('YYYY-MM-DD HH:mm:ss');
    const endDateStr = dayjs(value[1]).format('YYYY-MM-DD HH:mm:ss');
    return `${startDateStr} - ${endDateStr}`;
  };

  // Calculate and display the actual time range
  const getActualTimeRange = () => {
    if (startMode === 'relative') {
      const calculatedStartDate = endDate ? endDate.subtract(relativeDays, 'day') : null;
      if (calculatedStartDate && endDate) {
        return `${calculatedStartDate.format('YYYY-MM-DD HH:mm:ss')} ≤ col < ${endDate.format('YYYY-MM-DD HH:mm:ss')}`;
      }
    } else if (startDate && endDate) {
      return `${startDate.format('YYYY-MM-DD HH:mm:ss')} ≤ col < ${endDate.format('YYYY-MM-DD HH:mm:ss')}`;
    }
    return 'No date range selected';
  };

  return (
    <div className="filter-bar">
      {filterOptions.map((filter) => {
        let component;
        switch (filter.type) {
          case "text":
            component = (
              <Input
                placeholder={filter.label}
                value={filters[filter.name] || ""}
                onChange={(e) => onFilterChange(filter.name, e.target.value)}
                disabled={isLoading}
                allowClear
                suffix={isLoading ? <LoadingOutlined spin /> : null}
              />
            );
            break;
          case "select":
            const options = filter.options?.map((option) => ({
              value: option,
              label: option,
            })) || [];
            
            // Check if this filter should be single select
            const isSingleSelect = filter.multiple === false;
            
            // Check if this filter is disabled
            const isDisabled = filter.disabled || isLoading;
            
            component = (
              <Select
                mode={isSingleSelect ? undefined : "multiple"}
                className="wrap-select fixed-select"
                placeholder={isDisabled && !isLoading ? "Select a system first" : filter.label}
                value={filters[filter.name] || (isSingleSelect ? undefined : [])}
                onChange={(value) => onFilterChange(filter.name, value)}
                style={{ width: "100%" }}
                maxTagCount={isSingleSelect ? undefined : "responsive"}
                maxTagTextLength={isSingleSelect ? undefined : 10}
                showSearch
                allowClear
                optionFilterProp="label"
                disabled={isDisabled}
                loading={isLoading}
                options={options}
              />
            );
            break;
          case "timerange":
            // Use an Ant Design Input component with a suffix icon.
            component = (
              <Input
                readOnly
                value={getTimeRangeDisplay(filter.name)}
                onClick={() => !isLoading && openTimeRangeModal(filter.name)}
                disabled={isLoading}
                suffix={isLoading ? <LoadingOutlined spin /> : <DownOutlined />}
                style={{ cursor: 'pointer' }}
              />
            );
            break;
          default:
            component = null;
        }
        return (
          <div key={filter.name} className="filter-item">
            <label style={{ display: 'block', marginBottom: '4px' }}>{filter.label}</label>
            {component}
          </div>
        );
      })}
      <div className="filter-buttons">
        <Button type="primary" onClick={onApplyFilters} loading={isLoading}>
          Apply Filters
        </Button>
        <Button
          onClick={handleClearAll}
          disabled={isLoading}
          style={{ marginLeft: 8 }}
        >
          Clear All
        </Button>
      </div>
      <div className="filter-item">
        <label>Sort by</label>
        <Select
          value={sortBy}
          onChange={(value) => {
            onSortChange(value, sortOrder);
          }}
          disabled={isLoading}
          style={{ width: 150 }}
        >
          {(sortOptions || [
            { value: "timestamp", label: "Timestamp" },
            { value: "max_confidence", label: "Max Confidence" },
          ]).map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
      </div>

      <div className="filter-item">
        <label>Order</label>
        <Select
          value={sortOrder}
          onChange={(value) => {
            onSortChange(sortBy, value);
          }}
          disabled={isLoading}
          style={{ width: 120 }}
        >
          <Option value="desc">Descending</Option>
          <Option value="asc">Ascending</Option>
        </Select>
      </div>
      {/* Time Range Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <EditOutlined style={{ marginRight: '8px' }} />
            Edit time range
          </div>
        }
        open={timeRangeModalVisible}
        onOk={handleTimeRangeApply}
        onCancel={() => setTimeRangeModalVisible(false)}
        okText="Apply"
        cancelText="Cancel"
        width={600}
        footer={[
          <Button key="cancel" onClick={() => setTimeRangeModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="apply" type="primary" onClick={handleTimeRangeApply}>
            Apply
          </Button>
        ]}
      >
        <div style={{ padding: '10px 0' }}>
          <div>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '8px' }}>RANGE TYPE</div>
              <Select 
                value={rangeType} 
                onChange={setRangeType}
                style={{ width: '100%' }}
              >
                <Option value="custom">Custom</Option>
              </Select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '16px', fontWeight: 500, marginBottom: '16px' }}>
                Configure custom time range
              </div>
              
              <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', fontSize: '12px', color: '#8c8c8c', marginBottom: '8px' }}>
                    START (INCLUSIVE) 
                    <span style={{ marginLeft: '4px', cursor: 'help' }}>ⓘ</span>
                  </div>
                  
                  <Select 
                    value={startMode} 
                    onChange={setStartMode}
                    style={{ width: '100%', marginBottom: '8px' }}
                  >
                    <Option value="relative">Relative Date/Time</Option>
                    <Option value="specific">Specific Date/Time</Option>
                  </Select>
                  
                  {startMode === 'relative' ? (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <InputNumber 
                        value={relativeDays} 
                        onChange={(value) => setRelativeDays(Number(value))} 
                        style={{ width: '100%' }}
                      />
                      <Select 
                        value={relativeUnit} 
                        onChange={setRelativeUnit}
                        style={{ width: '100%' }}
                      >
                        <Option value="Days Before">Days Before</Option>
                        <Option value="Weeks Before">Weeks Before</Option>
                        <Option value="Months Before">Months Before</Option>
                      </Select>
                    </div>
                  ) : (
                    <DatePicker
                      showTime
                      value={startDate}
                      onChange={setStartDate}
                      style={{ width: '100%' }}
                      format="YYYY-MM-DD HH:mm:ss"
                    />
                  )}
                </div>
                
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', fontSize: '12px', color: '#8c8c8c', marginBottom: '8px' }}>
                    END (EXCLUSIVE)
                    <span style={{ marginLeft: '4px', cursor: 'help' }}>ⓘ</span>
                  </div>
                  
                  <Select 
                    value={endMode} 
                    onChange={(value: any) => {}} // Only specific mode is supported for end date
                    style={{ width: '100%', marginBottom: '8px' }}
                  >
                    <Option value="specific">Specific Date/Time</Option>
                  </Select>
                  
                  <DatePicker
                    showTime
                    value={endDate}
                    onChange={setEndDate}
                    style={{ width: '100%' }}
                    format="YYYY-MM-DD HH:mm:ss"
                  />
                </div>
              </div>
              
              <div style={{ marginTop: '24px' }}>
                <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                  Actual time range
                </div>
                <div>{getActualTimeRange()}</div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default FilterBar;